import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getApiBaseUrl, getImageUrl } from '../utils/api';
// import { formatPrice as formatPriceWithCurrency } from '../utils/currency';
import ImageViewerModal from './salon/ImageViewerModal';
import './salon/styles/image-viewer.css';
import './PublicCatalogue.css';

const RESERVED_SLUGS = new Set([
  'login', 'register', 'forgot-password', 'reset-password', 'verify-email',
  'dashboard', 'create-salon', 'salon', 'privacy-policy', 'terms-and-conditions',
  'pricing', 'api', 'uploads'
]);

function PublicCatalogue() {
  const { salonSlug } = useParams();
  const [state, setState] = useState({ status: 'loading', salon: null, dresses: [], error: null });
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerCurrentIndex, setViewerCurrentIndex] = useState(0);
  const [tryOnModalOpen, setTryOnModalOpen] = useState(false);
  const [tryOnDress, setTryOnDress] = useState(null);
  const [tryOnFile, setTryOnFile] = useState(null);
  const [tryOnLoading, setTryOnLoading] = useState(false);
  const [tryOnResult, setTryOnResult] = useState(null);
  const [tryOnError, setTryOnError] = useState(null);

  useEffect(() => {
    const slug = (salonSlug || '').toLowerCase().trim();
    if (!slug || RESERVED_SLUGS.has(slug)) {
      setState({ status: 'notfound', salon: null, dresses: [], error: null });
      return;
    }

    const base = getApiBaseUrl();
    fetch(`${base}/api/public/catalogue/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (res.status === 404) {
          setState({ status: 'notfound', salon: null, dresses: [], error: null });
          return null;
        }
        if (!res.ok) throw new Error('Failed to load catalogue');
        return res.json();
      })
      .then((data) => {
        if (data) {
          setState({ status: 'ok', salon: data.salon, dresses: data.dresses || [], error: null });
        }
      })
      .catch((err) => {
        setState({ status: 'error', salon: null, dresses: [], error: err.message });
      });
  }, [salonSlug]);

  // Unique title & meta description for this catalogue (distinct from the main site / index.html).
  useEffect(() => {
    if (state.status !== 'ok' || !state.salon) {
      return undefined;
    }

    const salonName = state.salon.name?.trim() || 'Salon';
    const pageTitle = `Try On Wedding Dresses Online · ${salonName}`;
    const pageDescription =
      `Explore the collection at ${salonName}: browse dresses, photos, and videos, then use AI try-on to preview how each look suits you—before you visit the salon.`;

    const previousTitle = document.title;
    document.title = `${pageTitle} | Dress Desk`;

    let metaDesc = document.querySelector('meta[name="description"]');
    const previousDescription = metaDesc?.getAttribute('content') ?? null;
    const createdMeta = !metaDesc;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', pageDescription);

    return () => {
      document.title = previousTitle;
      if (createdMeta && metaDesc?.parentNode) {
        metaDesc.parentNode.removeChild(metaDesc);
      } else if (metaDesc && previousDescription !== null) {
        metaDesc.setAttribute('content', previousDescription);
      }
    };
  }, [state.status, state.salon]);

  const slug = (salonSlug || '').toLowerCase().trim();
  if (!slug || RESERVED_SLUGS.has(slug)) {
    return <Navigate to="/" replace />;
  }

  if (state.status === 'loading') {
    return (
      <div className="public-catalogue">
        <div className="public-catalogue-loading">Loading...</div>
      </div>
    );
  }

  if (state.status === 'notfound') {
    return (
      <div className="public-catalogue">
        <div className="public-catalogue-notfound">Catalogue not found.</div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="public-catalogue">
        <div className="public-catalogue-error">Something went wrong. Please try again later.</div>
      </div>
    );
  }

  const { salon, dresses } = state;
  // const currency = salon.currency || 'EUR';

  const handleDressImageClick = (dress) => {
    if (!dress.images || dress.images.length === 0) return;
    const images = [...dress.images];
    if (dress.video_url) {
      const videoUrl = getImageUrl(dress.video_url);
      images.push({ type: 'video', url: videoUrl, thumbnail: videoUrl });
    }
    if (dress.video2_url) {
      const video2Url = getImageUrl(dress.video2_url);
      images.push({ type: 'video', url: video2Url, thumbnail: video2Url });
    }
    setViewerImages(images);
    setViewerCurrentIndex(0);
    setImageViewerOpen(true);
  };

  const openTryOnModal = (dress, e) => {
    if (e) e.stopPropagation();
    setTryOnDress(dress);
    setTryOnFile(null);
    setTryOnResult(null);
    setTryOnError(null);
    setTryOnModalOpen(true);
  };

  const closeTryOnModal = () => {
    setTryOnModalOpen(false);
    setTryOnDress(null);
    setTryOnFile(null);
    setTryOnResult(null);
    setTryOnError(null);
  };

  const handleTryOnSubmit = async () => {
    if (!tryOnFile || !tryOnDress || !slug) return;
    setTryOnLoading(true);
    setTryOnError(null);
    setTryOnResult(null);
    const base = getApiBaseUrl();
    const formData = new FormData();
    formData.append('bridePhoto', tryOnFile);
    formData.append('dressId', String(tryOnDress.id));
    try {
      const res = await fetch(`${base}/api/public/catalogue/${encodeURIComponent(slug)}/ai-try-on`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTryOnError(data.error || 'Try-on failed');
        return;
      }
      if (data.image) setTryOnResult(data.image);
      else setTryOnError('No image returned');
    } catch (err) {
      setTryOnError(err.message || 'Try-on failed');
    } finally {
      setTryOnLoading(false);
    }
  };

  return (
    <div className="public-catalogue">
      <header className="public-catalogue-header">
        <h1 className="public-catalogue-title">{salon.name}</h1>
        {salon.description && (
          <p className="public-catalogue-description">{salon.description}</p>
        )}
        <a href="/" className="public-catalogue-brand">Dress Desk</a>
      </header>

      <main className="public-catalogue-main">
        {dresses.length === 0 ? (
          <p className="public-catalogue-empty">No dresses in this catalogue yet.</p>
        ) : (
          <div className="public-catalogue-grid">
            {dresses.map((dress) => (
                  <article
                    key={dress.id}
                    className="public-catalogue-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDressImageClick(dress)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDressImageClick(dress);
                      }
                    }}
                    aria-label={`View images of ${dress.name}`}
                  >
                <div
                  className="public-catalogue-card-image-wrap"
                  role="button"
                  tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDressImageClick(dress);
                      }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDressImageClick(dress); } }}
                  aria-label={`View images of ${dress.name}`}
                >
                  {dress.images && dress.images[0] ? (
                    <img
                      src={getImageUrl(dress.image_small || dress.images[0])}
                      alt={dress.name}
                      className="public-catalogue-card-image"
                    />
                  ) : (
                    <div className="public-catalogue-card-placeholder">No image</div>
                  )}
                  {dress.images && dress.images.length > 0 && (
                    <button
                      type="button"
                      className="public-catalogue-try-on-btn"
                      onClick={(e) => openTryOnModal(dress, e)}
                      aria-label={`AI Try On: ${dress.name}`}
                    >
                      AI Try On
                    </button>
                  )}
                </div>
                <div className="public-catalogue-card-body">
                  <h2 className="public-catalogue-card-name">{dress.name}</h2>
                  {/* {dress.brand_name && (
                    <p className="public-catalogue-card-brand">{dress.brand_name}</p>
                  )} */}
                  {/* <div className="public-catalogue-card-prices">
                    {dress.rental_price != null && Number(dress.rental_price) > 0 && (
                      <span className="public-catalogue-card-price">
                        Rent: {formatPriceWithCurrency(dress.rental_price, currency, true, 0)}
                      </span>
                    )}
                    {dress.selling_price != null && Number(dress.selling_price) > 0 && (
                      <span className="public-catalogue-card-price">
                        Sale: {formatPriceWithCurrency(dress.selling_price, currency, true, 0)}
                      </span>
                    )}
                  </div> */}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      <footer className="public-catalogue-footer">
        <a href="/">{salon.name}</a> · Powered by <a href="/">Dress Desk</a>
      </footer>

      <ImageViewerModal
        isOpen={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        images={viewerImages}
        currentIndex={viewerCurrentIndex}
      />

      {tryOnModalOpen && (
        <div className="public-catalogue-try-on-overlay" role="dialog" aria-modal="true" aria-labelledby="try-on-modal-title">
          <div className="public-catalogue-try-on-modal">
            <button
              type="button"
              className="public-catalogue-try-on-close-x"
              onClick={closeTryOnModal}
              disabled={tryOnLoading}
              aria-label="Close AI Try On"
            >
              ×
            </button>
            <h2 id="try-on-modal-title" className="public-catalogue-try-on-title">AI Try On</h2>
            {!tryOnResult ? (
              <>
                <p className="public-catalogue-try-on-instructions">
                  Upload a clear, high-resolution full-body photo of yourself for best results.
                </p>
                <div className="public-catalogue-try-on-upload">
                  <input
                    type="file"
                    id="try-on-file"
                    accept="image/*"
                    onChange={(e) => { setTryOnFile(e.target.files?.[0] || null); setTryOnError(null); }}
                    disabled={tryOnLoading}
                  />
                  {tryOnFile && <span className="public-catalogue-try-on-filename">{tryOnFile.name}</span>}
                </div>
                {tryOnError && <p className="public-catalogue-try-on-error">{tryOnError}</p>}
                {tryOnLoading && <p className="public-catalogue-try-on-loading">Generating try-on...</p>}
                <div className="public-catalogue-try-on-actions">
                  <button type="button" className="public-catalogue-try-on-cancel" onClick={closeTryOnModal} disabled={tryOnLoading}>
                    Cancel
                  </button>
                  <button type="button" className="public-catalogue-try-on-submit" onClick={handleTryOnSubmit} disabled={!tryOnFile || tryOnLoading}>
                    Try On
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="public-catalogue-try-on-result-wrap">
                  <img src={tryOnResult} alt="AI Try On result" className="public-catalogue-try-on-result-img" />
                </div>
                <button type="button" className="public-catalogue-try-on-close" onClick={closeTryOnModal}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicCatalogue;
